import { Button, IconButton } from '@web/components/atoms/Button'
import { useBrandingsStore } from '@web/components/utils/branding/useBrandingsStore'
import React from 'react'

type PageButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  name: 'Primary' | 'Secondary'
  size?: 'xs' | 'sm' | 'md' | 'lg'
  iconSlot?: React.ReactElement
  buttonText?: string | React.ReactNode
  isLoading?: boolean
  loadingText?: string
  width?: 'auto' | '100%'
  bgColorOverride?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  styleOverride?: any
  asChild?: boolean
}

const PageButton = ({
  name,
  size = 'md',
  buttonText,
  width,
  isLoading,
  iconSlot,
  bgColorOverride,
  styleOverride,
  ...buttonProps
}: PageButtonProps) => {
  const { buttonColors, font } = useBrandingsStore(({ buttonData }) => buttonData)
  const { variant } = buttonColors[name]
  const cssPropertyName = name.toLowerCase()
  const styleProps = {
    ...buttonProps,
    size,
    borderRadius: 'var(--branding-button-radius)',
    fontFamily: font.font,
    fontWeight: 600,
    variant,
    width,
    loading: isLoading,
    ...(size === 'xs' ? {} : { height: styleOverride?.height ?? '50px', fontSize: '18px' }),
    ...(variant === 'outline'
      ? {
          border: '1px solid',
          borderColor: styleOverride?.borderColor ?? `var(--branding-button-${cssPropertyName}-bg-color)`,
          color: styleOverride?.color ?? `var(--branding-button-${cssPropertyName}-bg-color)`,
          _hover: {
            borderColor: `var(--branding-button-${cssPropertyName}-hover-bg-color)`,
            color: `var(--branding-button-${cssPropertyName}-hover-bg-color)`,
          },
        }
      : {
          bgColor: 'var(--branding-button-primary-bg-color)',
          color: 'var(--branding-button-primary-text-color, #FFFFFF)',
          _hover: {
            bgColor: 'var(--branding-button-primary-hover-bg-color)',
            color: 'var(--branding-button-primary-hover-text-color, #FFFFFF)',
          },
        }),
  }

  if (iconSlot && !buttonText) {
    return (
      <IconButton buttonAppearance="unstyled" {...styleProps} aria-label="icon-button" css={styleOverride}>
        {iconSlot}
      </IconButton>
    )
  }

  return (
    <Button {...styleProps} buttonAppearance="unstyled" style={styleOverride}>
      {iconSlot} {buttonText}
    </Button>
  )
}

export default PageButton
